import { forwardRef, useState } from "react";
import "./AppPasswordInput.scss";

export interface AppPasswordInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  error?: boolean;
}

const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

// The passed-in `className` (e.g. "form-control") lands on the *wrapper*,
// not the native input, so this always looks and behaves exactly like a
// plain input at its call site: same background, border, padding, height,
// color, and font as every sibling field, in whatever theme that page or
// modal applies to ".form-control" (bootstrap default, a dashboard modal's
// dark theme, LoginPage's black/gold theme, etc). The input and toggle
// button themselves are unstyled/transparent and just inherit from it.
const AppPasswordInput = forwardRef<HTMLInputElement, AppPasswordInputProps>(
  ({ className, error = false, disabled, ...rest }, ref) => {
    const [visible, setVisible] = useState(false);

    return (
      <div
        className={[className, "app-password-input", error ? "is-invalid" : ""]
          .filter(Boolean)
          .join(" ")}
      >
        <input
          {...rest}
          ref={ref}
          disabled={disabled}
          type={visible ? "text" : "password"}
        />
        <button
          type="button"
          className="app-password-toggle"
          disabled={disabled}
          onClick={() => setVisible((previous) => !previous)}
          aria-label={visible ? "Hide password" : "Show password"}
          aria-pressed={visible}
        >
          {visible ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
    );
  },
);

AppPasswordInput.displayName = "AppPasswordInput";

export default AppPasswordInput;
